/**
 * CSV parser + per-row validator for the ECL upload flow.
 *
 * Mapping CSV → API row:
 * - Hashed PII produced client-side via `hashing.ts` (plaintext PII never
 *   leaves the browser).
 * - Auto-fills transaction_id from a stable seed when the CSV omits it, so
 *   resubmits after an all-or-nothing failure don't double-count Google's
 *   dedup-by-(transaction_id, conversion_action_id) catches them.
 * - Rejects files >2000 rows (Google's per-request events:ingest cap).
 *
 * Output is a `ParseResult` with one accepted-or-rejected outcome per row;
 * the wizard shows this as the FlouriValidation step before any Google call.
 */
import type { OfflineConversionEvent } from '@/services/api/offlineConversions';
import { hashEmail, hashPhone, hashName } from './hashing';
import {
  deriveTransactionId,
  normalizeCurrency,
  parseConversionValue,
  toRfc3339Utc,
} from './normalizers';

export interface RowError {
  rowIndex: number; // 0-based, NOT counting the header row
  field?: string;
  reason: string;
}

export interface ParseResult {
  events: OfflineConversionEvent[];
  /** One entry per rejected row; accepted rows are absent. */
  rejections: RowError[];
  totalDataRows: number;
}

/** Hard cap per Google's events:ingest quota. */
export const MAX_ROWS_PER_FILE = 2000;

const KNOWN_COLUMNS = new Set([
  'email',
  'phone',
  'address_first_name',
  'address_last_name',
  'address_country_code',
  'address_postal_code',
  'gclid',
  'gbraid',
  'wbraid',
  'event_timestamp',
  'conversion_value',
  'currency',
  'transaction_id',
]);

const REQUIRED_COLUMNS = ['event_timestamp'];

export interface ParseInput {
  csvText: string;
  conversionActionId: string;
}

/**
 * Parse a CSV string and produce ECL events. PII is SHA-256 hashed in this
 * function — the returned events contain hashed values only.
 */
export async function parseCsv({
  csvText,
  conversionActionId,
}: ParseInput): Promise<ParseResult> {
  const rows = splitCsv(csvText);
  if (rows.length === 0) {
    throw new Error('CSV is empty.');
  }

  const header = rows[0].map((h) => h.trim().toLowerCase());
  const dataRows = rows.slice(1).filter((r) => r.some((c) => c.trim() !== ''));

  for (const required of REQUIRED_COLUMNS) {
    if (!header.includes(required)) {
      throw new Error(`Missing required column: ${required}`);
    }
  }

  // Address group is all-or-nothing
  const hasAnyAddressColumn = [
    'address_first_name',
    'address_last_name',
    'address_country_code',
    'address_postal_code',
  ].some((c) => header.includes(c));
  const hasAllAddressColumns = [
    'address_first_name',
    'address_last_name',
    'address_country_code',
    'address_postal_code',
  ].every((c) => header.includes(c));
  if (hasAnyAddressColumn && !hasAllAddressColumns) {
    throw new Error(
      'Address columns are all-or-nothing: include all of address_first_name, address_last_name, address_country_code, address_postal_code, or omit them all.'
    );
  }

  if (dataRows.length > MAX_ROWS_PER_FILE) {
    throw new Error(
      `CSV has ${dataRows.length} rows; the Data Manager API caps events:ingest at ${MAX_ROWS_PER_FILE} per request. Split the file.`
    );
  }

  const unknown = header.filter((h) => !KNOWN_COLUMNS.has(h));
  if (unknown.length > 0) {
    // Log-only — we don't reject on unknown columns (forward-compat with
    // future schema additions), and surfacing them in the UI is a follow-up.
    // For now, devs can inspect the browser console when investigating an
    // unexpectedly-shaped CSV.
    console.warn('Unknown CSV columns ignored:', unknown);
  }

  const events: OfflineConversionEvent[] = [];
  const rejections: RowError[] = [];

  for (let i = 0; i < dataRows.length; i++) {
    const row = dataRows[i];
    const get = (col: string): string => {
      const idx = header.indexOf(col);
      return idx >= 0 && row[idx] !== undefined ? row[idx].trim() : '';
    };

    const rawEmail = get('email');
    const rawPhone = get('phone');
    const eventTimestampRaw = get('event_timestamp');
    const rfc3339 = toRfc3339Utc(eventTimestampRaw);
    if (!rfc3339) {
      rejections.push({
        rowIndex: i,
        field: 'event_timestamp',
        reason: `Invalid event_timestamp: "${eventTimestampRaw}". Must be parseable as a date.`,
      });
      continue;
    }

    const event: OfflineConversionEvent = { event_timestamp: rfc3339 };

    // Kick off all per-row hashes eagerly so SHA-256 work overlaps within
    // the row. Email and name never throw; phone validates and throws on
    // bad format — we keep its catch separate so we can attribute the
    // rejection to the `phone` field.
    //
    // Future: bounded concurrency across rows (process N rows in parallel)
    // would be the larger win on 2000-row uploads. Within-row parallelism
    // here is the cheap part of Copilot's recommendation.
    const emailPromise = rawEmail ? hashEmail(rawEmail) : Promise.resolve(undefined);
    const phonePromise = rawPhone ? hashPhone(rawPhone) : Promise.resolve(undefined);

    let addressPromise: Promise<typeof event.hashed_address> = Promise.resolve(undefined);
    if (hasAllAddressColumns) {
      const fn = get('address_first_name');
      const ln = get('address_last_name');
      const cc = get('address_country_code').toUpperCase();
      const pc = get('address_postal_code');
      if (fn && ln && cc && pc) {
        if (!/^[A-Z]{2}$/.test(cc)) {
          rejections.push({
            rowIndex: i,
            field: 'address_country_code',
            reason: 'Must be ISO 3166-1 alpha-2 (two letters).',
          });
          continue;
        }
        addressPromise = Promise.all([hashName(fn), hashName(ln)]).then(
          ([hfn, hln]) => ({
            hashed_first_name: hfn,
            hashed_last_name: hln,
            country_code: cc,
            postal_code: pc,
          }),
        );
      }
    }

    // Await sequentially so the existing per-field error attribution stays.
    try {
      event.hashed_email = await emailPromise;
    } catch (err) {
      rejections.push({ rowIndex: i, field: 'email', reason: (err as Error).message });
      continue;
    }
    try {
      event.hashed_phone = await phonePromise;
    } catch (err) {
      rejections.push({ rowIndex: i, field: 'phone', reason: (err as Error).message });
      continue;
    }
    event.hashed_address = await addressPromise;

    const gclid = get('gclid');
    const gbraid = get('gbraid');
    const wbraid = get('wbraid');
    if (gclid) event.gclid = gclid;
    if (gbraid) event.gbraid = gbraid;
    if (wbraid) event.wbraid = wbraid;

    // At least one identifier required
    if (
      !event.gclid &&
      !event.gbraid &&
      !event.wbraid &&
      !event.hashed_email &&
      !event.hashed_phone &&
      !event.hashed_address
    ) {
      rejections.push({
        rowIndex: i,
        reason:
          'Row needs at least one identifier (email, phone, address, gclid, gbraid, or wbraid).',
      });
      continue;
    }

    const conversionValueRaw = get('conversion_value');
    if (conversionValueRaw) {
      const value = parseConversionValue(conversionValueRaw);
      if (value === null) {
        rejections.push({
          rowIndex: i,
          field: 'conversion_value',
          reason: 'Must be a non-negative number.',
        });
        continue;
      }
      event.conversion_value = value;

      const currency = normalizeCurrency(get('currency'));
      if (!currency) {
        rejections.push({
          rowIndex: i,
          field: 'currency',
          reason: 'currency is required when conversion_value is set (e.g. "USD").',
        });
        continue;
      }
      event.currency = currency;
    }

    // transaction_id: take user value if present, else derive a stable one
    const txnIn = get('transaction_id');
    if (txnIn) {
      event.transaction_id = txnIn;
    } else {
      event.transaction_id = await deriveTransactionId(
        { email: rawEmail || null, phone: rawPhone || null, event_timestamp: rfc3339 },
        conversionActionId
      );
    }

    events.push(event);
  }

  return { events, rejections, totalDataRows: dataRows.length };
}

/**
 * Minimal RFC 4180-ish CSV splitter. Handles double-quoted cells with
 * embedded commas/newlines and escaped quotes (`""`). Good enough for the
 * CRM exports the wizard targets; we explicitly don't try to be a full
 * CSV library here.
 */
export function splitCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (c === '"') {
        inQuotes = false;
      } else {
        cell += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(cell);
      cell = '';
    } else if (c === '\n' || c === '\r') {
      // Treat LF, CR, and CRLF as row delimiters. For CRLF, the '\n' that
      // immediately follows a '\r' would re-trigger the row push and emit a
      // spurious empty row — skip it.
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
      if (c === '\r' && text[i + 1] === '\n') {
        i++;
      }
    } else {
      cell += c;
    }
  }
  // last cell + row
  if (cell !== '' || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}

/**
 * Produce the user-downloadable template content. ECL-first column order;
 * GCLID / GBRAID / WBRAID at the end since they're optional.
 */
export function buildCsvTemplate(): string {
  return [
    'email,phone,address_first_name,address_last_name,address_country_code,address_postal_code,event_timestamp,conversion_value,currency,transaction_id,gclid,gbraid,wbraid',
    'jane@example.com,+14155551234,Jane,Doe,US,94016,2026-06-14T12:00:00Z,49.99,USD,lead-12345,,,',
  ].join('\n');
}
